import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ReceiptText,
  Truck,
  Calculator,
  MapPinned,
  User,
  Users,
  Plus,
  Menu,
  LogOut,
  Crown,
  Cloud,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useProStatus } from "@/hooks/useProStatus";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { mediaUrl } from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/receipts", label: "Receipts", icon: ReceiptText },
  { to: "/hauls", label: "Hauls", icon: Truck },
  { to: "/taxes", label: "Taxes & IFTA", icon: Calculator },
  { to: "/map", label: "Commercial Map", icon: MapPinned },
  { to: "/fleet", label: "Fleet", icon: Users },
  { to: "/profile", label: "Profile", icon: User },
  { to: "/settings", label: "Settings", icon: Settings },
];

const TITLES = [
  ["/dashboard", "Dashboard"],
  ["/receipts", "Receipts"],
  ["/add", "Add Expense"],
  ["/hauls", "Hauls"],
  ["/taxes", "Taxes & IFTA"],
  ["/map", "Commercial Map"],
  ["/fleet", "Fleet"],
  ["/admin", "Admin Console"],
  ["/profile", "Profile"],
  ["/settings", "Settings"],
  ["/upgrade-success", "RigBooks Pro"],
  ["/upgrade", "RigBooks Pro"],
];

function pageTitle(pathname) {
  const hit = TITLES.find(([p]) => pathname === p || pathname.startsWith(p + "/"));
  return hit ? hit[1] : "RigBooks";
}

function ProCard({ pro }) {
  const navigate = useNavigate();
  if (pro?.is_pro) {
    return (
      <div className="rounded-xl border border-rig-accent/40 bg-rig-accent/10 p-4">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-rig-accent" />
          <span className="font-display text-sm font-bold uppercase tracking-widest text-rig-accent">
            Pro Active
          </span>
        </div>
        <p className="mt-1 text-xs text-rig-dim">Unlimited receipts + IFTA + CPA sharing.</p>
      </div>
    );
  }
  const remaining = pro?.remaining;
  return (
    <div className="rounded-xl border border-rig-border bg-rig-surface2 p-4">
      <div className="font-display text-sm font-bold uppercase tracking-widest text-white">
        Free Plan
      </div>
      {remaining != null && (
        <p className="mt-1 text-xs text-rig-dim">
          {remaining} of {pro?.free_limit} receipts left this month
        </p>
      )}
      <Button
        data-testid="sidebar-upgrade-btn"
        onClick={() => navigate("/upgrade")}
        className="mt-3 h-9 w-full bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
      >
        Upgrade to Pro
      </Button>
    </div>
  );
}

function SidebarBody({ onNavigate }) {
  const { data: pro } = useProStatus();
  const { user } = useAuth();
  const nav = user?.is_admin
    ? [...NAV, { to: "/admin", label: "Admin Console", icon: ShieldCheck }]
    : NAV;
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-6 pt-6">
        <Link to="/dashboard" onClick={onNavigate}>
          <Logo size={34} />
        </Link>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {nav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                isActive
                  ? "bg-rig-accent/12 text-rig-accent shadow-[inset_2px_0_0_#00E676]"
                  : "text-rig-dim hover:bg-rig-surface2 hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-5 w-5 ${isActive ? "text-rig-accent" : ""}`} strokeWidth={2.2} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4">
        <ProCard pro={pro} />
      </div>
    </div>
  );
}

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = (user?.name || user?.email || "D").slice(0, 1).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-rig-bg">
      {/* Static sidebar (desktop) */}
      <aside className="hidden w-64 shrink-0 border-r border-rig-border bg-rig-surface lg:block">
        <div className="sticky top-0 h-screen">
          <SidebarBody />
        </div>
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 border-rig-border bg-rig-surface p-0">
          <SidebarBody onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-rig-border bg-rig-bg/85 px-4 backdrop-blur-md sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-2xl font-bold uppercase tracking-[0.14em] text-white">
            {pageTitle(location.pathname)}
          </h1>
          <div className="ml-auto flex items-center gap-3">
            <div
              className="hidden items-center gap-1.5 rounded-full border border-rig-border bg-rig-surface px-3 py-1.5 sm:flex"
              data-testid="cloud-sync-chip"
            >
              <Cloud className="h-3.5 w-3.5 text-rig-success" />
              <span className="font-display text-[10px] font-bold uppercase tracking-widest text-rig-success">
                Synced
              </span>
            </div>
            <Button
              data-testid="topbar-add-receipt"
              onClick={() => navigate("/add")}
              className="h-10 bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
            >
              <Plus className="mr-1 h-4 w-4" strokeWidth={3} /> New Receipt
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="outline-none">
                  <Avatar className="h-10 w-10 border border-rig-borderStrong">
                    {user?.picture && <AvatarImage src={mediaUrl(user.picture)} alt={user?.name || "user"} />}
                    <AvatarFallback className="bg-rig-surface2 font-display font-bold text-rig-accent">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-rig-border bg-rig-surface">
                <DropdownMenuLabel className="text-white">
                  <div className="truncate text-sm">{user?.name || "Driver"}</div>
                  <div className="truncate text-xs font-normal text-rig-dim">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-rig-border" />
                <DropdownMenuItem
                  onClick={() => navigate("/profile")}
                  className="cursor-pointer text-white focus:bg-rig-surface2"
                >
                  <User className="mr-2 h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  data-testid="menu-settings"
                  className="cursor-pointer text-white focus:bg-rig-surface2"
                >
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="signout-btn"
                  onClick={handleLogout}
                  className="cursor-pointer text-rig-error focus:bg-rig-surface2 focus:text-rig-error"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-6xl animate-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
